import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Feature } from '../types';

const FeaturesSection = () => {
  const [hoveredFeature, setHoveredFeature] = useState<string | null>(null);
  
  const features: Feature[] = [
    {
      id: "1",
      icon: "BarChart3",
      title: "Real-Time Analytics",
      description: "Track campaign performance with detailed analytics dashboards. Monitor views, engagement, and ROI in real-time with comprehensive reporting tools.",
      gradient: "from-primary to-red-600"
    },
    {
      id: "2",
      icon: "Target",
      title: "Smart Matching",
      description: "AI-powered algorithm matches brands with the perfect creators based on audience demographics, engagement rates, and content style.",
      gradient: "from-red-600 to-orange-500"
    },
    {
      id: "3",
      icon: "DollarSign",
      title: "Pay Per View",
      description: "Revolutionary pricing model where brands only pay for actual views delivered. Creators earn fairly based on their performance and reach.",
      gradient: "from-green-500 to-emerald-600"
    },
    {
      id: "4",
      icon: "Shield",
      title: "Secure Payments",
      description: "Protected payment system with escrow services. Funds are held securely and released automatically when campaign milestones are met.",
      gradient: "from-blue-500 to-purple-600"
    },
    {
      id: "5",
      icon: "Users",
      title: "Creator Network",
      description: "Access to thousands of verified creators across all niches. Build long-term partnerships with top-performing influencers in your industry.",
      gradient: "from-purple-500 to-pink-600"
    },
    {
      id: "6",
      icon: "Zap",
      title: "Instant Campaigns",
      description: "Launch campaigns in minutes with our streamlined process. Quick approval system gets your campaigns live and reaching audiences fast.",
      gradient: "from-yellow-500 to-orange-600"
    }
  ];

  return (
  <section className="py-32 sm:py-40 lg:py-48 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
  {/* Enhanced Background Elements */}
  <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl animate-float"></div>
  <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-red-600/5 rounded-full blur-3xl animate-float-delayed"></div>
  <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] bg-orange-500/5 rounded-full blur-2xl animate-pulse"></div>
  
  {/* Decorative Shapes */}
  <div className="absolute top-32 right-16 w-24 h-24 border-4 border-primary/10 rounded-full animate-float"></div>
  <div className="absolute bottom-48 left-20 w-20 h-20 bg-gradient-to-br from-primary/10 to-red-600/10 rounded-2xl rotate-45 animate-float-delayed"></div>
  <div className="absolute top-1/2 left-10 w-16 h-16 border-4 border-dashed border-orange-500/20 rounded-lg rotate-12 animate-float"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Enhanced Header Section */}
        <div className="text-center mb-24">
          <div className="inline-flex items-center gap-3 px-7 py-3.5 bg-gradient-to-r from-primary via-red-500 to-orange-500 rounded-full mb-10 shadow-2xl hover:shadow-primary/50 transition-all duration-300 hover:scale-105 cursor-pointer animate-fade-in">
            <Icon name="Sparkles" size={32} className="text-white animate-pulse" />
            <span className="text-lg font-black text-white uppercase tracking-wide">Platform Features</span>
            <Icon name="Sparkles" size={32} className="text-white animate-pulse" />
          </div>
          <h2 className="text-5xl sm:text-6xl lg:text-8xl font-black text-black mb-10 tracking-tight leading-[1.1] animate-fade-in" style={{ animationDelay: '0.1s' }}>
            Why Choose{' '}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-primary via-red-500 to-orange-500 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
                NOONX
              </span>
              <div className="absolute -bottom-4 left-0 right-0 h-3 bg-gradient-to-r from-primary/20 via-red-500/20 to-orange-500/20 rounded-full blur-sm"></div>
            </span>
            ?
          </h2>
          <p className="text-xl sm:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed font-semibold animate-fade-in" style={{ animationDelay: '0.2s' }}>
            Everything you need to run <span className="text-primary font-black">successful influencer marketing campaigns</span> with complete transparency and control.
          </p>
        </div>

        {/* Enhanced Features Grid with Bento Box Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className="relative group animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
              onMouseEnter={() => setHoveredFeature(feature.id)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              {/* Glow Effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} rounded-3xl blur-2xl opacity-0 group-hover:opacity-30 transition-all duration-500 scale-95 group-hover:scale-100`}></div>
              
              {/* Card */}
              <div className="relative bg-white rounded-3xl border-2 border-gray-100 p-10 hover:border-transparent transition-all duration-500 group-hover:scale-105 group-hover:shadow-2xl overflow-hidden">
                {/* Animated Background Pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                
                {/* Decorative Corner Accent */}
                <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${feature.gradient} opacity-10 rounded-bl-full transition-all duration-500 group-hover:w-32 group-hover:h-32`}></div>
                
                {/* Icon with Enhanced Animation */}
                <div className="relative mb-8">
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} rounded-2xl blur-lg opacity-0 group-hover:opacity-50 transition-opacity duration-500`}></div>
                  <div className={`relative w-24 h-24 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-xl`}>
                    <Icon name={feature.icon} size={48} className="text-white" />
                  </div>
                  {/* Floating Badge */}
                  {hoveredFeature === feature.id && (
                    <div className="absolute -top-2 -right-2 bg-white border-2 border-gray-200 rounded-full px-3 py-1 shadow-lg animate-bounce">
                      <span className="text-xs font-black text-primary">✨</span>
                    </div>
                  )}
                </div>

                {/* Title with Gradient on Hover */}
                <h3 className={`relative text-2xl font-black text-black mb-6 transition-all duration-300 ${
                  hoveredFeature === feature.id ? 'bg-gradient-to-r ' + feature.gradient + ' bg-clip-text text-transparent' : ''
                }`}>
                  {feature.title}
                </h3>

                {/* Description */}
                <p className="relative text-gray-700 leading-relaxed text-base mb-8 font-medium">
                  {feature.description}
                </p>

                {/* Enhanced Learn More Link */}
                <button className={`relative flex items-center gap-3 font-bold text-base transition-all duration-300 group-hover:gap-5 bg-gradient-to-r ${feature.gradient} bg-clip-text text-transparent`}>
                  Learn more
                  <div className={`p-2 bg-gradient-to-br ${feature.gradient} rounded-full transition-transform duration-300 group-hover:rotate-45`}>
                    <Icon name="ArrowRight" size={16} className="text-white" />
                  </div>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Enhanced Bottom CTA Banner with Playful Design */}
        <div className="animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <div className="relative bg-gradient-to-r from-black via-gray-900 to-black rounded-3xl p-12 sm:p-20 shadow-2xl overflow-hidden group hover:scale-[1.02] transition-all duration-500">
            {/* Animated Background Patterns */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-red-600/10 to-orange-500/10 bg-[length:200%_auto] animate-gradient"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(228,35,19,0.1),transparent_50%)]"></div>
            
            {/* Floating Shapes */}
            <div className="absolute top-10 right-20 w-16 h-16 bg-primary/20 rounded-full blur-xl animate-float"></div>
            <div className="absolute bottom-10 left-20 w-20 h-20 bg-red-600/20 rounded-full blur-xl animate-float-delayed"></div>
            <div className="absolute top-1/2 left-1/3 w-24 h-24 border-4 border-primary/10 rounded-full animate-float"></div>
            
            <div className="relative flex flex-col lg:flex-row items-center justify-between gap-12">
              <div className="flex items-center gap-10 text-white">
                <div className="relative group/icon">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary to-red-600 rounded-3xl blur-xl opacity-50 group-hover/icon:opacity-75 transition-opacity"></div>
                  <div className="relative w-28 h-28 bg-gradient-to-br from-primary via-red-600 to-orange-500 backdrop-blur-sm rounded-3xl flex items-center justify-center shadow-2xl group-hover/icon:rotate-12 transition-all duration-500 animate-float border-4 border-white/10">
                    <Icon name="Rocket" size={56} className="text-white" />
                  </div>
                </div>
                <div className="text-left">
                  <h4 className="text-4xl sm:text-5xl font-black mb-3 bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent">
                    Ready to Get Started?
                  </h4>
                  <p className="text-white/80 text-xl font-semibold">
                    Join <span className="text-primary font-black">500+</span> brands and creators already using NOONX
                  </p>
                  <div className="flex items-center gap-4 mt-4">
                    <div className="flex -space-x-3">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-red-600 border-2 border-black"></div>
                      ))}
                    </div>
                    <span className="text-white/60 text-sm font-semibold">and counting...</span>
                  </div>
                </div>
              </div>
              <button className="group/btn relative px-14 py-6 bg-white text-black rounded-2xl font-black text-xl hover:bg-primary hover:text-white transition-all duration-300 shadow-2xl hover:shadow-white/40 hover:scale-110 whitespace-nowrap overflow-hidden">
                <span className="relative z-10 flex items-center gap-3">
                  Get Started Free
                  <Icon name="ArrowRight" size={24} className="group-hover/btn:translate-x-2 transition-transform duration-300" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-primary to-red-600 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-300 rounded-2xl"></div>
              </button>
            </div>
            
            {/* Additional Trust Elements */}
            <div className="relative mt-12 flex flex-wrap items-center justify-center gap-8 pt-8 border-t border-white/10">
              <div className="flex items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer">
                <Icon name="CheckCircle" size={20} className="text-green-400" />
                <span className="text-sm font-semibold">No credit card required</span>
              </div>
              <div className="flex items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer">
                <Icon name="Zap" size={20} className="text-yellow-400" />
                <span className="text-sm font-semibold">Setup in 2 minutes</span>
              </div>
              <div className="flex items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer">
                <Icon name="Users" size={20} className="text-blue-400" />
                <span className="text-sm font-semibold">Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;